package kz.dentalcrm.app.data

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import kotlinx.datetime.atTime
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.core.utils.map
import kz.dentalcrm.app.data.network.MainApi
import kz.dentalcrm.app.data.network.request_model.AppointmentRequest
import kz.dentalcrm.app.data.network.response_model.AppointmentResponse
import kz.dentalcrm.app.data.network.response_model.CashBox
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.data.network.response_model.PatientTreatment
import kz.dentalcrm.app.data.network.response_model.PatientsResponse
import kz.dentalcrm.app.data.network.response_model.employees.EmployeesResponse
import kz.dentalcrm.app.domain.AppointmentRepository

class AppointmentRepositoryImpl(
    private val mainApi: MainApi
) : AppointmentRepository {

    override suspend fun getAppointments(
        dateFrom: LocalDate,
        dateTo: LocalDate,
        employeeId: String?
    ): Result<List<AppointmentResponse>> {
        return mainApi.getAppointments(
            dateFrom = LocalDate(
                dateFrom.year, dateFrom.month, dateFrom.day
            ).atTime(LocalTime(0, 0, 0)).toString(),
            dateTo =  LocalDate(
                dateTo.year, dateTo.month, dateTo.day
            ).atTime(LocalTime(23, 59, 59)).toString(),
            employeeId = employeeId,
        )
    }

    override suspend fun addPatient(
        name: String,
        surname: String,
        phone: String
    ): Result<PatientData?> {
        return mainApi.addPatient(
            name = name,
            surname = surname,
            phone = phone
        )
    }

    override suspend fun getPatients(page: Int): Result<PatientsResponse?> {
        return mainApi.getPatients(page = page)
    }

    override suspend fun getPatient(id: Int): Result<PatientData?> {
        return mainApi.getPatient(id = id)
    }

    override suspend fun searchPatientByFilter(
        name: String,
        surname: String,
        phone: String,
    ): Result<PatientsResponse?> {
        return mainApi.searchPatientByFilter(
            name = name,
            surname = surname,
            phone = phone
        )
    }

    override suspend fun searchPatients(
        search: String,
        page: Int
    ): Result<PatientsResponse> {
        return mainApi.search(
            search = search,
            page = page
        )
    }

    override suspend fun getEmployees(page: Int, pageSize: Int): Result<EmployeesResponse?> {
        return mainApi.getEmployees(page, pageSize)
    }

    override suspend fun searchEmployees(page: Int, search: String) {
        TODO("Not yet implemented")
    }

    override suspend fun createAppointment(appointmentRequest: AppointmentRequest): Result<Any?> {
        return mainApi.createAppointment(appointmentRequest)
    }

    override suspend fun deleteAppointment(id: Int): Result<Any?> {
        return mainApi.deleteAppointment(id = id)
    }

    override suspend fun updatePatient(patientData: PatientData): Result<Any?> {
        return mainApi.updatePatient(
            id = patientData.id,
            patientData = patientData
        )
    }

    override suspend fun deletePatient(id: Int): Result<Any?> {
        return mainApi.deletePatient(id = id)
    }

    override suspend fun getPositions(): Result<List<kz.dentalcrm.app.data.network.response_model.employees.Position>> {
        return mainApi.getPositions()
    }

    override suspend fun getServicesArray(): Result<List<kz.dentalcrm.app.data.network.response_model.services.Service>> {
        return mainApi.getServicesArray()
    }

    override suspend fun addEmployee(
        employeeBody: kz.dentalcrm.app.data.network.request_model.AddEmployeeBody
    ): Result<Any?> {
        return mainApi.addEmployee(employeeBody)
    }

    override suspend fun getPermissions(): Result<List<kz.dentalcrm.app.data.network.response_model.permissions.Permission>> {
        return mainApi.getPermissions()
    }

    override suspend fun getEmployee(id: Int): Result<kz.dentalcrm.app.data.network.response_model.employees.Employee?> {
        return mainApi.getEmployee(id)
    }

    override suspend fun deleteEmployee(id: Int): Result<Any?> {
        return mainApi.deleteEmployee(id)
    }

    override suspend fun updateEmployee(id: Int, employeeBody: kz.dentalcrm.app.data.network.request_model.AddEmployeeBody): Result<Any?> {
        return mainApi.updateEmployee(id, employeeBody)
    }

    override suspend fun getAppointment(id: Int): Result<AppointmentResponse?> {
        return mainApi.getAppointment(id)
    }

    override suspend fun updateAppointment(id: Int, appointmentRequest: AppointmentRequest): Result<Any?> {
        return mainApi.updateAppointment(id, appointmentRequest)
    }

    override suspend fun getCashBoxes(): Result<List<CashBox>> {
        return mainApi.getCashBoxes()
    }

    override suspend fun getAppointmentTreatments(appointmentId: Int): Result<List<PatientTreatment>> {
        return mainApi.getAppointmentTreatments(appointmentId)
    }

    override suspend fun getPatientLastAppointments(patientId: Int): Result<List<AppointmentResponse>> {
        return mainApi.getPatientLastAppointments(patientId)
    }
}